import type { OscillatorType } from "./types";
import { createPulseOscillator } from "./PulseOscillator";

// A uniform interface over "something that produces a tone", so Voice can treat
// a plain OscillatorNode and the custom pulse oscillator identically.
export interface VoiceSource {
  output: AudioNode;
  frequency: AudioParam;
  detune: AudioParam;
  /** Only present for the pulse oscillator. */
  width?: AudioParam;
  start(when: number): void;
  stop(when: number): void;
}

export function createSource(ctx: BaseAudioContext, type: OscillatorType): VoiceSource {
  if (type === "pulse") {
    return createPulseOscillator(ctx);
  }
  if (type === "noise") {
    return createNoiseSource(ctx);
  }
  const osc = new OscillatorNode(ctx, { type });
  return {
    output: osc,
    frequency: osc.frequency,
    detune: osc.detune,
    start: (when) => osc.start(when),
    stop: (when) => osc.stop(when),
  };
}

// White noise — the raw material for hats, snares, claps, wind. The timbre is
// shaped by the patch's filter + amp envelope (e.g. highpass + short decay = hat,
// bandpass = snare). Pitch is irrelevant, so frequency/detune are inert stand-ins
// (real AudioParams from an unconnected gain node) and the engine can write to
// them harmlessly.
const noiseCache = new WeakMap<BaseAudioContext, AudioBuffer>();

function noiseBuffer(ctx: BaseAudioContext): AudioBuffer {
  let buf = noiseCache.get(ctx);
  if (buf) return buf;
  const len = Math.floor(ctx.sampleRate * 2);
  buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  noiseCache.set(ctx, buf);
  return buf;
}

function createNoiseSource(ctx: BaseAudioContext): VoiceSource {
  const src = new AudioBufferSourceNode(ctx, { buffer: noiseBuffer(ctx), loop: true });
  const inert = new GainNode(ctx); // its .gain is a valid but unused AudioParam
  return {
    output: src,
    frequency: inert.gain,
    detune: inert.gain,
    start: (when) => src.start(when),
    stop: (when) => src.stop(when),
  };
}
